import { useApolloClient } from "@apollo/client";
import { LoadingSpinner } from "@msys/ui";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useEffect } from "react";
import { DEFAULT_INVOICE_PAYMENT_TERM_DURATION } from "../../utils.js";
import {
  splitMissingFields,
  SERVICE_PERIOD_MISSING_KEY,
} from "./eInvoicePreconditionMessage.js";
import {
  InvoiceDetails__InvoiceFragment,
  useOpenInvoiceMutation,
} from "./Invoices.generated.js";

const MISSING_FIELD_LABELS = {
  // precondition check keys (org settings validation)
  billingAddress: "missingField.billingAddress",
  format: "missingField.format",
  fullLegalName: "missingField.fullLegalName",
  iban: "missingField.iban",
  vatOrTaxNumber: "missingField.vatOrTaxNumber",
  deliveryOfService: "missingField.deliveryOfService",
  // Zod validation paths — seller
  "seller.legalName": "missingField.seller.legalName",
  "seller.vatNumber": "missingField.seller.vatNumber",
  "seller.iban": "missingField.seller.iban",
  "seller.address.streetLine": "missingField.seller.address.streetLine",
  "seller.address.postalCode": "missingField.seller.address.postalCode",
  "seller.address.city": "missingField.seller.address.city",
  "seller.address.countryCode": "missingField.seller.address.countryCode",
  // Zod validation paths — buyer
  "buyer.name": "missingField.buyer.name",
  "buyer.vatNumber": "missingField.buyer.vatNumber",
  "buyer.contact.email": "missingField.buyer.contact.email",
  "buyer.address.streetLine": "missingField.buyer.address.streetLine",
  "buyer.address.postalCode": "missingField.buyer.address.postalCode",
  "buyer.address.city": "missingField.buyer.address.city",
  "buyer.address.countryCode": "missingField.buyer.address.countryCode",
  // Zod validation paths — invoice header
  "invoice.number": "missingField.invoice.number",
  "invoice.issueDate": "missingField.invoice.issueDate",
} as const;

const FAILED_WORKFLOW_STATUSES: ReadonlySet<string> = new Set([
  "FAILED",
  "CANCELED",
  "TIMED_OUT",
  "TERMINATED",
]);

export const InvoiceDocumentDisplay = ({
  invoice,
}: {
  invoice: InvoiceDetails__InvoiceFragment;
}) => {
  const { t } = useTranslate("Invoices");
  const status = invoice.documentGeneration?.status;
  const isGenerating =
    status === "RUNNING" || (status == null && !invoice.file);

  if (isGenerating) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
        data-testid="invoice-document-generating"
      >
        {/* `LoadingSpinner` defaults to `flex: 1` (and `padding: 2`), which
            would make it grow to fill this column and push the text to the
            bottom. Override back to `flex: 0` so the text sits directly below
            the spinner (the old `flex={0}` behaviour before the MUI 9 / @msys/ui
            migration removed those props). */}
        <LoadingSpinner sx={{ flex: 0, padding: 0 }} />
        <Typography variant="body2" color="textSecondary">
          {t("Generating documents…")}
        </Typography>
      </Box>
    );
  }

  if (status != null && FAILED_WORKFLOW_STATUSES.has(status)) {
    return <InvoiceDocumentGenerationError invoice={invoice} />;
  }

  // Prefer the ZUGFeRD e-Rechnung in the viewer: it is a PDF (visually
  // identical to the plain PDF) but carries the embedded XML, so the viewer's
  // own download button yields a valid e-Rechnung. XRechnung (XML) formats and
  // invoices without an e-invoice fall back to the plain PDF.
  const viewerUrl =
    invoice.eInvoiceFile?.mimeType === "application/pdf"
      ? invoice.eInvoiceFile.url
      : invoice.file?.url;

  return (
    <iframe
      src={viewerUrl}
      title={"PDF"}
      style={{ width: "100%", height: "100%", border: "none" }}
    />
  );
};

const InvoiceDocumentGenerationError = ({
  invoice,
}: {
  invoice: InvoiceDetails__InvoiceFragment;
}) => {
  const { t } = useTranslate("Invoices");
  const client = useApolloClient();
  const [openInvoice, { loading: isRetrying }] = useOpenInvoiceMutation({
    client,
  });

  const error = invoice.documentGeneration?.error;
  const status = invoice.documentGeneration?.status;
  const detailsPayload =
    (error?.code === "E_INVOICE_PRECONDITIONS_NOT_MET" ||
      error?.code === "E_INVOICE_MAPPING_FAILED") &&
    Array.isArray(error.details)
      ? (error.details[0] as { missingFields?: string[] } | undefined)
      : undefined;
  const missingFields = detailsPayload?.missingFields ?? null;
  const isPreconditions = error?.code === "E_INVOICE_PRECONDITIONS_NOT_MET";
  // The service period is a per-invoice field; the rest are organisation
  // settings. Keep them apart so the per-invoice field is never framed as (or
  // listed among) the organisation fields.
  const { servicePeriodMissing, orgFields } = splitMissingFields(missingFields);
  // Fields shown in the bulleted list: org-only for preconditions (the service
  // period gets its own line), all fields for a mapping failure.
  const listedFields = isPreconditions ? orgFields : (missingFields ?? []);

  useEffect(() => {
    if (error?.message) {
      console.warn("Invoice document generation error:", error.message);
    }
  }, [error?.message]);

  const getErrorMessage = (): string => {
    if (error?.code === "E_INVOICE_PRECONDITIONS_NOT_MET") {
      // Organisation-level fields drive the main "configure in the organisation
      // settings" message; when only the per-invoice service period is missing,
      // the main message is the per-invoice one (and it gets a dedicated line
      // below whenever it is missing alongside org fields).
      if (orgFields.length === 0)
        return t(SERVICE_PERIOD_MISSING_KEY, { ns: "Invoices" });
      return t(
        "E-invoice could not be generated. Please configure in the organisation settings:"
      );
    }
    if (error?.code === "E_INVOICE_MAPPING_FAILED")
      return t(
        "E-invoice data could not be mapped. Please check the invoice data."
      );
    if (status === "TIMED_OUT")
      return t("Document generation timed out. Please try again.");
    if (status === "CANCELED" || status === "TERMINATED")
      return t("Document generation was cancelled.");
    return t("An unexpected error occurred during document generation.");
  };
  const translatedMessage = getErrorMessage();

  const handleRetry = async () => {
    try {
      await openInvoice({
        variables: {
          input: {
            projectId: invoice.project.id,
            docId: invoice.quote.id,
            invoiceId: invoice.id,
            dueDate: invoice.dueDate
              ? moment(invoice.dueDate).format("YYYY-MM-DD")
              : moment()
                  .add(
                    invoice.paymentTermDuration ||
                      DEFAULT_INVOICE_PAYMENT_TERM_DURATION,
                    "seconds"
                  )
                  .format("YYYY-MM-DD"),
            deliveryOfServiceStart: invoice.deliveryOfServiceStart
              ? moment(invoice.deliveryOfServiceStart).format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD"),
            // Default a missing end to the start date (not today) so a
            // start-only service period stays a single delivery date (BT-72)
            // and is never silently widened into a range (BG-14).
            deliveryOfServiceEnd: invoice.deliveryOfServiceEnd
              ? moment(invoice.deliveryOfServiceEnd).format("YYYY-MM-DD")
              : invoice.deliveryOfServiceStart
                ? moment(invoice.deliveryOfServiceStart).format("YYYY-MM-DD")
                : moment().format("YYYY-MM-DD"),
          },
        },
        context: { noErrorNotification: true },
      });
    } catch {
      // Mutation failure surfaces through the polling path —
      // the workflow will fail and this component re-renders with the error.
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid="invoice-document-failed"
    >
      <Alert severity="error" sx={{ maxWidth: 480, width: "100%" }}>
        <AlertTitle>{t("Document generation failed")}</AlertTitle>
        <Typography variant="body2" gutterBottom>
          {translatedMessage}
        </Typography>
        {/* When the per-invoice service period is missing alongside org fields,
            give it its own line (the main message above covers the org fields).
            When it is the only missing field, the main message already says it. */}
        {isPreconditions && servicePeriodMissing && orgFields.length > 0 ? (
          <Typography variant="body2" gutterBottom>
            {t(SERVICE_PERIOD_MISSING_KEY, { ns: "Invoices" })}
          </Typography>
        ) : null}
        {listedFields.length > 0 ? (
          <>
            <Typography variant="body2">
              {isPreconditions
                ? t("Please complete the following organisation fields:")
                : t("The following fields are missing or invalid:")}
            </Typography>
            <ul>
              {listedFields.map(field => {
                const key =
                  MISSING_FIELD_LABELS[
                    field as keyof typeof MISSING_FIELD_LABELS
                  ];
                return <li key={field}>{key ? t(key) : field}</li>;
              })}
            </ul>
          </>
        ) : null}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleRetry}
            disabled={isRetrying}
            data-testid="invoice-document-retry"
          >
            {t("Retry")}
          </Button>
        </Box>
      </Alert>
    </Box>
  );
};
